import React from "react";
import {
  Card,
  BlockStack,
  InlineStack,
  Text,
  Badge,
  ProgressBar,
  Avatar,
  Box,
  Divider,
  Icon,
  FormLayout,
  InlineGrid,
  Tooltip,
} from "@shopify/polaris";
import { ArrowUpIcon, CalendarIcon, EditIcon } from "@shopify/polaris-icons";
import { useParams, useNavigate } from "react-router-dom";

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);

export default function ProjectCard({ project, mode = "compact" }) {
  const {
    logo,
    name,
    status,
    start_date,
    deadline,
    project_progress,
    client_satisfaction,
    price,
    current_cost,
    current_profit,
    team,
    people,
    id,
  } = project;

  const statusColor =
    String(status.id) === "2"
      ? "warning"
      : String(status.id) === "3"
        ? "info"
        : String(status.id) === "4"
          ? "success"
          : "default";
  const navigate = useNavigate();
  const groupByDepartment = (people) => {
    return people.reduce((acc, person) => {
      const deptName = person.department?.name || "Unknown";

      if (!acc[deptName]) {
        acc[deptName] = [];
      }

      acc[deptName].push({
        name: person.name,
        photo: person.image_url,
      });

      return acc;
    }, {});
  };
  console.log("groupByDepartment=", groupByDepartment(people));
  const flipDate = (iso) => {
    if (!iso) return "";
    const [y, m, d] = iso.split("-");
    return `${d}-${m}-${y}`;
  };
  return (
    <>
      <BlockStack gap="400">
        {/* Header */}

        {/* Header */}
        <InlineStack
          align="space-between"
          blockAlign="center"
          wrap={false}
          style={{ width: "100%", alignItems: "center" }}
        >
          {/* LEFT SIDE: logo + fixed-width name */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              minWidth: 0, // ✅ allows truncation
              flex: 1,
            }}
          >
            {logo && <Avatar size="md" source={logo} />}
            <div
              style={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              <Text
                as="span" // ⭐ Important
                variant="headingMd"
                fontWeight="semibold"
              >
                {name}
              </Text>
            </div>

            {/* <Text variant="headingMd" as="h6" fontWeight="semibold" truncate>
              <span style={{ fontSize: "1rem" }}>
                {name?.length > 40 ? name.slice(0, 40) + "…" : name}
              </span>
            </Text> */}
          </div>

          {/* RIGHT SIDE: status + edit */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexShrink: 0, // ✅ stays fixed
            }}
          >
            <Badge tone={statusColor}>{status.description}</Badge>
            <div
              style={{ cursor: "pointer" }}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/admin/projects/${id}`);
              }}
            >
              <Icon
                source={EditIcon}
                accessibilityLabel="Edit project"
                tone="base"
              />
            </div>
          </div>
        </InlineStack>

        <ProgressBar progress={project_progress} size="small" tone="success" />

        {/* Dates */}
        <InlineStack gap="400" align="space-between">
          <InlineStack gap="200" blockAlign="center">
            <Icon source={CalendarIcon} />
            <Box>
              <Text as="span" fontWeight="medium">
                Start
              </Text>
              <Text as="p" tone="subdued">
                {flipDate(start_date)}
              </Text>
            </Box>
          </InlineStack>

          <InlineStack gap="200" blockAlign="center">
            <Icon source={CalendarIcon} />
            <Box>
              <Text as="span" fontWeight="medium">
                Deadline
              </Text>
              <Text as="p" tone="subdued">
                {flipDate(deadline)}
              </Text>
            </Box>
          </InlineStack>
        </InlineStack>

        {/* Normal / Expanded */}
        {mode !== "compact" && (
          <>
            <Divider />
            <BlockStack gap="200">
              <InlineStack align="space-between">
                {project_progress !== null && (
                  <Text tone="subdued">
                    Progress{" "}
                    <Text fontWeight="medium">{project_progress}%</Text>
                  </Text>
                )}

                {client_satisfaction !== null && (
                  <Text tone="subdued">
                    Client Satisfaction{" "}
                    <Text fontWeight="medium">{client_satisfaction}%</Text>
                  </Text>
                )}
              </InlineStack>

              <InlineStack align="space-between">
                {/* <Text>
                  <Text tone="subdued">Price</Text>
                  <Text fontWeight="medium">{formatCurrency(price)}</Text>
                </Text> */}
                {current_cost !== null && (
                  <Text>
                    <Text tone="subdued">Cost</Text>
                    <Text fontWeight="medium">
                      {formatCurrency(current_cost)}
                    </Text>
                  </Text>
                )}
                {current_profit !== null && (
                  <Text>
                    {/* <InlineStack gap="100" blockAlign="center">
                    <Icon source={ArrowUpIcon} tone="success" /> */}
                    <Text tone="subdued">Current Profit </Text>
                    {/* </InlineStack> */}
                    <Text fontWeight="medium" tone="success">
                      {formatCurrency(current_profit)}
                    </Text>
                  </Text>
                )}
              </InlineStack>
            </BlockStack>
          </>
        )}
        {/* EXPANDED */}
        {mode === "expanded" && people && (
          <>
            <Divider />
            <Box paddingBlockStart="200">
              <FormLayout>
                {/* ✅ InlineGrid creates dynamic 2-column layout */}
                <InlineGrid columns={{ xs: 1, sm: 2 }} gap="500">
                  {Object.entries(groupByDepartment(people)).map(
                    ([role, members]) => (
                      // <Card key={role} roundedAbove="sm">
                      <BlockStack gap="200">
                        <Text tone="subdued">{role}</Text>

                        <InlineStack gap="200" wrap>
                          {members.map((member, idx) => (
                            <Tooltip content={member.name}>
                              <Avatar
                                key={idx}
                                name={member.name}
                                source={member.photo}
                                size="md"
                              />
                            </Tooltip>
                          ))}
                        </InlineStack>
                      </BlockStack>
                      // </Card>
                    ),
                  )}
                </InlineGrid>
              </FormLayout>
            </Box>
          </>
        )}

        {mode !== "list" && <></>}
      </BlockStack>
    </>
  );
}
